<template>
  <input
    :type="foreign ? 'text' : 'tel'"
    inputmode="tel"
    class="form-control"
    autocomplete="off"
    :maxlength="foreign ? 20 : null"
    :value="display"
    @input="onInput"
    @paste="onPaste"
    @focus="keepCaretAfterPrefix"
    @click="keepCaretAfterPrefix"
    @keydown="onKeydown"
  >
</template>

<script>
import {
  PHONE_PREFIX,
  PHONE_MASK_LENGTH,
  isForeignPhone,
  nationalDigits,
  applyMask,
  toModel,
  caretAfterDigits
} from '@/utils/phone'

// Bare masked <input> for Australian mobiles ("+61 ___ ___ ___"): the prefix is fixed, the user
// types only the 9 national digits. Other attributes (id, class, required, disabled) fall through
// to the input, so it drops into legacy markup that owns its own label/tooltip.
// Non-AU values (legacy "+7…") are shown and edited as plain text so they are never mangled.
export default {
  name: 'PhoneInput',
  props: {
    value: { default: '' }
  },
  data() {
    return { maskLength: PHONE_MASK_LENGTH }
  },
  computed: {
    foreign() {
      return isForeignPhone(this.value)
    },
    display() {
      return this.foreign ? this.value : applyMask(nationalDigits(this.value))
    }
  },
  methods: {
    onInput(event) {
      const el = event.target
      if (this.foreign) {
        // Unmasked legacy number: still only "+", digits and spaces — no letters or other symbols
        const clean = el.value.replace(/[^\d+ ]/g, '')
        if (clean !== el.value) el.value = clean
        this.$emit('input', clean)
        return
      }
      const caret = el.selectionStart
      const digitsBeforeCaret = nationalDigits(el.value.slice(0, caret == null ? el.value.length : caret)).length
      this.apply(el, nationalDigits(el.value), digitsBeforeCaret)
    },
    onPaste(event) {
      if (this.foreign) return
      const pasted = (event.clipboardData || window.clipboardData).getData('text')
      const digits = nationalDigits(pasted)
      // A whole number (any common shape) replaces the value; short fragments are inserted natively
      if (digits.length >= 9) {
        event.preventDefault()
        this.apply(event.target, digits, digits.length)
      }
    },
    apply(el, digits, caretDigits) {
      const masked = applyMask(digits)
      el.value = masked
      const pos = caretAfterDigits(Math.min(caretDigits, digits.length))
      el.setSelectionRange(pos, pos)
      this.$emit('input', toModel(digits))
    },
    keepCaretAfterPrefix(event) {
      if (this.foreign) return
      const el = event.target
      // Defer: the browser places the caret after focus/click handlers run
      setTimeout(() => {
        if (el.selectionStart < PHONE_PREFIX.length && el.selectionEnd < PHONE_PREFIX.length) {
          el.setSelectionRange(PHONE_PREFIX.length, PHONE_PREFIX.length)
        }
      }, 0)
    },
    onKeydown(event) {
      if (this.foreign) return
      const el = event.target
      const atPrefix = el.selectionStart === el.selectionEnd && el.selectionStart <= PHONE_PREFIX.length
      if (event.key === 'Backspace' && atPrefix) event.preventDefault()
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/FormFields/PhoneInput.vue